import { MouseEvent } from 'react';
import { RecentEntitiesModel } from 'core/services/recent-entities-service/recent-entities-model';

export type PlacerXTRAHost = 'chains' | 'property' | 'advanced-reports';

export type LinkType = 'download' | 'tableau' | 'onlinemap';

export type TableActiveTab = 'completed' | 'shared' | 'progress' | 'adminView';

export type Attachment = {
    id: string;
    content_url?: string;
    file_name: string;
    mapped_content_url?: string;
};

export type SetSharedClosedReportDataProps = {
    id: number;
    target: string;
    sharedWith: string[];
};

export interface OnlineReportPopupProps {
    onClose: (e: MouseEvent) => void;
    onClick: (e: MouseEvent, path: string | undefined, linkType: LinkType) => void;
    reportName: string;
    pathName: string;
    user?: string;
}

export interface Image {
    order?: number;
    title: string;
    image_url: string;
}

export interface OfflineReportListItem {
    id: string;
    name: string;
    description: string;
    tags: string[];
    primary_image: Image;
    use_cases: string[];
    sample_address: string;
    /**
     * If premium user and sample_report is available, a URL string will be returned.
     * Otherwise, null is the way to go.
     */
    sample_report: string | undefined;
    popularity_score: number;
    price: number;
}

export interface OfflineReportDetails extends OfflineReportListItem {
    report_entity_types: Array<'venue' | 'chain' | 'complex' | 'billboard'>;
    report_entity_group_size: 'single' | 'dual';
    sub_images: Image[];
    delivery_time_range: {
        max: number;
        type: 'hours' | 'days' | 'weeks' | 'months';
        min: number;
    };
    formats: string[];
    form?: Field[];
    minimum_report_period_in_days: number;
    maximum_report_period_in_days: number;
}

export interface Entity {
    id: string;
    name: string;
    address: string;
}

export type KnownEntity = {
    type: RecentEntitiesModel['type'] | 'recent';
    id: string;
};

export type UnknownEntity = {
    type: 'freetext';
    address: string;
    name: string;
};

export type AnEntity = KnownEntity | UnknownEntity;

export interface RequestObject {
    accountId: string;
    entityGroupSize: 'single' | 'dual';
    entities: [Entity, Entity?];
    subject: string;
    /**
     * YYYY-MM-DD
     */
    reportStartDate: string;
    /**
     * YYYY-MM-DD
     */
    reportEndDate: string;

    /**
     * * This is for the dynamic form fields -
     *  */
    formFields: Record<string, string>;
}

export type FieldType =
    | 'text'
    | 'text_area'
    | 'file'
    | 'multiselect'
    | 'select'
    | 'radio'
    | 'number';

export type Field =
    | TextField
    | FileField
    | MultiselectField
    | SelectField
    | RadioField
    | NumberField;

export interface BaseField {
    type: FieldType;
    name: string;
    required: boolean;
}

export interface UserAccountDetailsItem {
    username: string;
    id: string;
}

export interface UserAccountDetails {
    members: UserAccountDetailsItem[];
    me: string;
    account_name?: string;
    error_code: number;
    result: string;
}

export type FieldWithDefaultValue<T = Field> = T extends { defaultValue?: string } ? T : never;

export type AttachmentItem = {
    url: string;
    fileName: string;
};

export type SharingTargetOptions = 'user' | 'account';

export interface TextField extends BaseField {
    type: 'text' | 'text_area';
    defaultValue?: string;
    placeholder?: string;
}

export interface FileField extends BaseField {
    type: 'file';
    formats: Array<'text/csv' | 'application/pdf' | 'image/png' | 'image/jpg' | 'image/jpeg'>;
}

export interface MultiselectField extends BaseField {
    type: 'multiselect';
    options: string[];
    defaultValue?: string;
}

export interface SelectField extends BaseField {
    type: 'select';
    options: string[];
    defaultValue: string;
    followers?: Array<{
        option: string;
        input: Field;
    }>;
}

export interface RadioField extends BaseField {
    type: 'radio';
    options: string[];
    defaultValue?: string;
}

export interface NumberField extends BaseField {
    type: 'number';
    defaultValue?: number;
}

export interface OrderManagementRequest {
    id: number;
    subject: string;
    entities: [Entity, Entity];
    reportDates: {
        from: string;
        to: string;
    };
    requestedOn: string;
    status: 'new' | 'open' | 'pending' | 'hold' | 'solved' | 'closed';
    reportName: string; //| null;
    thumbnail: Image | null;
    reportId: string | null;
    readReceipt: boolean;
    comments: Comment[];
    tablouLink: string | null;
    mapsLink: string | null;
    zipFile: boolean;
    sharedWith?: string[];
    target?: SharingTargetOptions;
    sharedBy?: string;
    sharedDate?: string;
    supportTeam?: string;
    requestorEmail?: string;
    poiDataList?: string;
}

export interface OfflineReportsConfiguration {
    most_popular_reports_count: number;
}

export interface OfflineReportsCreditsBalance {
    account_credit_balance: number;
    credit_balance: number;
    error_code: number;
    result: string;
    user_weekly_balance: number;
}

export interface Comment {
    author_meta: {
        email: string;
        id: number;
        name: string;
        photo: string | null;
        role: string;
        fromName?: string;
        fromAddress?: string;
    };
    attachments: Attachment[];
    created_at: string;
    id: number;
    html_body: string;
}

export interface NewCommentPayload {
    comment: string;
}

export interface EmailMessagePayloadWithAttachments {
    caseId: string;
    comment: string;
    files: File[];
    requestSubject?: string;
}

export interface ShareReportPayload {
    target: string;
    users: string[];
    email_message?: string;
    shared_users_emails?: string[];
    report_name?: string;
}

export interface SupportRequest {
    comments?: Comment[];
    deliveryETA: string;
    id: string;
    lastModified: string;
    readReceipt: boolean;
    requestedOn: string;
    requestorEmail?: string;
    subject: string;
    status:
        | 'New'
        | 'In Progress'
        | 'Data Entry'
        | 'Pending'
        | 'On Hold'
        | 'Reopen'
        | 'Solved'
        | 'Closed';
    supportCaseCategory: string;
    supportTeam: string;
}

export const REQUEST_TABLE_IN_PROGRESS_TAB_NAME: string = 'progress';
export const REQUEST_TABLE_SHARED_WITH_ME_TAB_NAME: string = 'shared';
export const REQUEST_TABLE_MY_REPORTS_TAB_NAME: string = 'completed';
export const REQUEST_TABLE_ADMIN_VIEW_TAB_NAME: string = 'adminView';
